// pages/ContributeKnowledgePage.js - Vollständig mit Analytics
import { useState, useEffect, useRef } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { collection, doc } from 'firebase/firestore'
import { app, db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import { useCompany } from '../contexts/CompanyContext'
import ImageHeader from '../components/ImageHeader'
import ProgressIndicator from '../components/contribute/ProgressIndicator'
import Step1ProblemDetails from '../components/contribute/Step1ProblemDetails'
import Step2Solution from '../components/contribute/Step2Solution'
import Step3AdditionalInfo from '../components/contribute/Step3AdditionalInfo'
import Step4Review from '../components/contribute/Step4Review'
import GamificationBadge from '../components/contribute/GamificationBadge'
import Leaderboard from '../components/contribute/Leaderboard'
import DraftsList from '../components/contribute/DraftsList'
import QuickAddModal from '../components/contribute/QuickAddModal'
import gamificationService from '../services/gamificationService'
import analyticsService from '../services/analyticsService'
import draftService from '../services/draftService'
import useDraftAutoSave from '../hooks/useDraftAutoSave'
import { usePageTracking, useFeatureTracking } from '../hooks/useAnalytics'
import LoadingSpinner from '../components/LoadingSpinner'
import { toast } from 'react-hot-toast'
import { getImageReferenceName, normalizeUmlauts } from '../utils/imageMentions'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import './ContributeKnowledgePage.css'

function ContributeKnowledgePage () {
  const { user } = useAuth()
  const { features, headerImageUrl, companyName } = useCompany()

  // Analytics Hooks
  usePageTracking()
  useFeatureTracking('knowledge_contribution')

  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 4
  const [userStats, setUserStats] = useState(null)
  const [stepStartTime, setStepStartTime] = useState(Date.now())
  const [formStartTime] = useState(Date.now())

  const [formData, setFormData] = useState({
    deviceName: '',
    category: 'troubleshooting',
    problemTitle: '',
    errorCode: '',
    problemDescription: '',
    solution: '',
    components: '',
    difficulty: 'medium',
    estimatedTime: '',
    toolsRequired: '',
    images: []
  })

  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [currentDraftId, setCurrentDraftId] = useState(null)
  const [showQuickAddModal, setShowQuickAddModal] = useState(false)
  const [loadingDraft, setLoadingDraft] = useState(false)
  const draftsListRefreshRef = useRef(null)

  // Auto-Save Hook
  const handleDraftIdChange = (newDraftId) => {
    setCurrentDraftId(newDraftId)
  }

  const handleDraftSaved = () => {
    // Refresh drafts list when draft is saved
    if (draftsListRefreshRef.current) {
      draftsListRefreshRef.current()
    }
  }

  const { saveStatus, lastSavedAt, isSaving, saveDraft } = useDraftAutoSave(
    formData,
    currentStep,
    currentDraftId,
    handleDraftIdChange,
    handleDraftSaved,
    submitting // Pause auto-save during submission
  )

  // Track form start
  useEffect(() => {
    analyticsService.trackContribution('started', {
      user_email: user?.email
    })

    // Track abandonment on page leave
    return () => {
      if (currentStep > 1 && !success) {
        analyticsService.trackContribution('abandoned', {
          currentStep,
          totalSteps,
          time_spent: Date.now() - formStartTime,
          completion_percentage: (currentStep / totalSteps) * 100
        })
      }
    }
  }, [])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)

    // Track important field changes
    if (['deviceName', 'category', 'difficulty'].includes(field)) {
      analyticsService.trackUserAction('form_field_changed', {
        field,
        value: field === 'deviceName' ? 'device_selected' : value,
        current_step: currentStep
      })
    }
  }

  const validateStep = step => {
    switch (step) {
      case 1:
        return formData.deviceName && formData.problemTitle
      case 2:
        return formData.solution
      case 3:
        return true
      case 4:
        return true
      default:
        return false
    }
  }

  const handleNext = () => {
    const stepDuration = Date.now() - stepStartTime

    if (validateStep(currentStep)) {
      // Track successful step completion
      analyticsService.trackContributionFunnel(currentStep, true)
      analyticsService.trackContribution('step_completed', {
        currentStep,
        step_duration_ms: stepDuration,
        deviceName: formData.deviceName,
        category: formData.category
      })

      setCurrentStep(prev => Math.min(prev + 1, totalSteps))
      setError(null)
      setStepStartTime(Date.now())
    } else {
      // Track validation failure
      analyticsService.trackContributionFunnel(currentStep, false)
      analyticsService.trackError('contribution_validation_error', {
        step: currentStep,
        missing_fields: getMissingFields(currentStep),
        severity: 'low'
      })
      setError('Bitte füllen Sie alle Pflichtfelder aus.')
    }
  }

  const getMissingFields = step => {
    const missing = []
    if (step === 1) {
      if (!formData.deviceName) missing.push('deviceName')
      if (!formData.problemTitle) missing.push('problemTitle')
    }
    if (step === 2) {
      if (!formData.solution) missing.push('solution')
    }
    return missing
  }

  const handleBack = () => {
    analyticsService.trackUserAction('step_back', {
      from_step: currentStep,
      to_step: currentStep - 1
    })

    setCurrentStep(prev => Math.max(prev - 1, 1))
    setError(null)
    setStepStartTime(Date.now())
  }

  const handleSubmit = async () => {
    // Prevent double submission
    if (submitting) {
      return
    }

    // WICHTIG: Submitting sofort setzen um Auto-Save zu stoppen
    // Dies verhindert, dass der Auto-Save Hook während des Submits läuft
    // und nach dem Löschen des Drafts einen neuen erstellt (Recovery-Logik)
    setSubmitting(true)

    const totalDuration = Date.now() - formStartTime

    if (!validateStep(1) || !validateStep(2)) {
      setError('Bitte füllen Sie alle Pflichtfelder aus.')
      analyticsService.trackError('contribution_submit_validation_error', {
        severity: 'low'
      })
      setSubmitting(false) // Reset bei Validierungsfehler
      return
    }

    // HINWEIS: Kein manueller saveDraft() mehr vor dem Submit nötig!
    // Die Form-Daten werden direkt an submitUnmoderatedKnowledge gesendet.
    // Das Backend verwendet nur noch die Form-Daten (keine Merge-Logik mehr).

    setError(null)

    // Track submission characteristics
    const submissionMetadata = {
      device_name: formData.deviceName,
      category: formData.category,
      difficulty: formData.difficulty,
      has_error_code: !!formData.errorCode,
      solution_length: formData.solution.length,
      has_components: !!formData.components,
      has_tools: !!formData.toolsRequired,
      total_duration_seconds: Math.round(totalDuration / 1000),
      user_email: user.email
    }

    analyticsService.trackContribution('submitting', submissionMetadata)

    try {
      // Generate knowledgeId upfront (Firestore-compatible ID)
      // This ensures images and knowledge document use the same ID
      const companyId = user.userData.companyId
      const knowledgeId = doc(collection(db, `companies/${companyId}/knowledge`)).id

      // Upload images to Firebase Storage first (if any)
      // Handle both file objects (new uploads) and URL objects (from drafts)
      let imageUrls = []
      if (formData.images && formData.images.length > 0) {
        const storage = getStorage(app)

        imageUrls = await Promise.all(
          formData.images.map(async (imageObj, index) => {
            // Check if image is already uploaded (has URL but no file property)
            if (imageObj.url && !imageObj.file) {
              // Image/Video/PDF is already uploaded (from draft), use existing URL
              const filename = imageObj.filename || imageObj.name || 'media'
              let mediaType = imageObj.mediaType
              if (!mediaType) {
                // Detect from filename or URL
                if (filename.match(/\.pdf$/i) || imageObj.url?.match(/\.pdf$/i)) {
                  mediaType = 'pdf'
                } else if (imageObj.url?.match(/\.(mp4|webm|mov)$/i) || filename.match(/\.(mp4|webm|mov)$/i)) {
                  mediaType = 'video'
                } else {
                  mediaType = 'image'
                }
              }
              return {
                url: imageObj.url,
                filename: filename,
                name: filename,
                size: imageObj.size || 0,
                uploadedAt: imageObj.uploadedAt || new Date().toISOString(),
                referenceName: imageObj.referenceName || getImageReferenceName(filename),
                mediaType: mediaType
              }
            }

            // Image is a new file object, needs to be uploaded
            if (!imageObj.file) {
              console.warn('Image object missing file property:', imageObj)
              return null
            }

            // Normalize filename (convert umlauts to ASCII)
            const originalFileName = imageObj.name || imageObj.file.name || 'image'
            const normalizedFileName = normalizeUmlauts(originalFileName)
            
            const timestamp = Date.now()
            const fileName = `${timestamp}_${index}_${normalizedFileName}`
            const storageRef = ref(
              storage,
              `knowledge-images/${companyId}/${knowledgeId}/${fileName}`
            )

            // Detect media type (image, video, or pdf)
            let mediaType = 'image'
            if (imageObj.file.type.startsWith('video/')) {
              mediaType = 'video'
            } else if (imageObj.file.type === 'application/pdf') {
              mediaType = 'pdf'
            }

            // Upload with explicit metadata to ensure contentType is set correctly
            // This helps Storage Rules recognize the file type
            let contentType = imageObj.file.type
            if (!contentType) {
              // Fallback based on mediaType
              if (mediaType === 'video') {
                contentType = 'video/mp4'
              } else if (mediaType === 'pdf') {
                contentType = 'application/pdf'
              } else {
                contentType = 'image/jpeg'
              }
            }
            await uploadBytes(storageRef, imageObj.file, {
              contentType: contentType
            })
            const url = await getDownloadURL(storageRef)

            return {
              url,
              filename: normalizedFileName, // Use normalized filename
              size: imageObj.size || imageObj.file.size || 0,
              uploadedAt: new Date().toISOString(),
              referenceName: imageObj.referenceName || getImageReferenceName(normalizedFileName),
              mediaType: mediaType // Add media type (image, video, or pdf)
            }
          })
        )

        // Filter out null values (invalid images)
        imageUrls = imageUrls.filter(img => img !== null)
      }

      const functions = getFunctions(app)
      const submitKnowledge = httpsCallable(
        functions,
        'submitUnmoderatedKnowledge'
      )

      await submitKnowledge({
        ...formData,
        draftId: currentDraftId || null, // Send draftId if exists - Function will load draft and create new knowledge
        knowledgeId: knowledgeId, // Send pre-generated knowledgeId for consistent image paths
        images: imageUrls, // Send image URLs instead of file objects
        submittedBy: user.email,
        submittedAt: new Date().toISOString()
      })

      // Draft wird in der Function gelöscht nach erfolgreichem Submit
      setCurrentDraftId(null)

      // Track successful submission
      analyticsService.trackContribution('submitted', submissionMetadata)
      analyticsService.trackConversion('knowledge_submitted', 1)

      // Gamification tracking
      try {
        const gamificationResult = await gamificationService.recordContribution(
          user.email,
          user.uid
        )

        if (gamificationResult.newBadges.length > 0) {
          analyticsService.trackUserAction('badges_unlocked', {
            badge_count: gamificationResult.newBadges.length,
            badge_ids: gamificationResult.newBadges.map(b => b.id)
          })
        }
      } catch (gamError) {
        console.error('Gamification tracking error:', gamError)
        analyticsService.trackError('gamification_error', {
          message: gamError.message,
          severity: 'low'
        })
      }

      setSuccess(true)
      //toast
      toast.success('Ihr Beitrag wurde erfolgreich eingereicht!')

      // Track form completion funnel
      analyticsService.trackContributionFunnel(4, true)

      // WICHTIG: Form-Daten SOFORT zurücksetzen, BEVOR submitting auf false gesetzt wird!
      // Dies verhindert, dass der Auto-Save Hook einen neuen Draft erstellt.
      setFormData({
        deviceName: '',
        category: 'troubleshooting',
        problemTitle: '',
        errorCode: '',
        problemDescription: '',
        solution: '',
        components: '',
        difficulty: 'medium',
        estimatedTime: '',
        toolsRequired: '',
        images: []
      })
      setCurrentStep(1)
      setCurrentDraftId(null)

      // Jetzt ist es sicher, submitting auf false zu setzen
      setSubmitting(false)

      // Reload nach kurzem Delay für bessere UX
      setTimeout(() => {
        setSuccess(false)
        window.location.reload()
      }, 1000)
    } catch (err) {
      console.error('Submission error:', err)

      analyticsService.trackError('contribution_submit_error', {
        message: err.message,
        code: err.code,
        ...submissionMetadata,
        severity: 'high'
      })

      setError('Fehler beim Einreichen: ' + err.message)
      setSubmitting(false) // Nur im Fehlerfall hier
    }
  }

  // Load draft into form
  const handleLoadDraft = async draft => {
    setLoadingDraft(true)
    
    try {
      // Wenn bereits ein aktiver Entwurf vorhanden ist, speichere ihn zuerst
      if (currentDraftId && currentDraftId !== draft.id) {
        // Prüfe ob es Daten gibt, die gespeichert werden sollten
        if (formData.deviceName && (formData.problemTitle || formData.solution)) {
          try {
            // Speichere den aktuellen Entwurf vor dem Wechsel
            // Der Hook prüft selbst, ob gerade gespeichert wird
            await saveDraft()
          } catch (error) {
            console.error('Error saving draft before switch:', error)
            // Weiter mit dem Wechsel, auch wenn Speichern fehlgeschlagen ist
          }
        }
      }

      // Filter images: Only load images that have a URL (already uploaded)
      // File objects can't be serialized and shouldn't be in drafts
      const draftImages = (draft.images || []).filter(img => {
        // Only keep images that have a URL property (already uploaded)
        return img && (img.url || (typeof img === 'string' && img.startsWith('http')))
      }).map((img, index) => {
        // Convert draft image format to ImageUploader format
        // Draft images have: {url, filename, size, uploadedAt}
        // ImageUploader expects: {url (as preview), name, size, id, referenceName}
        
        if (typeof img === 'string') {
          // If it's just a URL string, convert it
          const filename = img.split('/').pop() || 'image'
          let mediaType = 'image'
          if (filename.match(/\.pdf$/i) || img.match(/\.pdf$/i)) {
            mediaType = 'pdf'
          } else if (img.match(/\.(mp4|webm|mov)$/i) || filename.match(/\.(mp4|webm|mov)$/i)) {
            mediaType = 'video'
          }
          return {
            url: img,
            preview: img,
            name: filename,
            filename: filename,
            size: 0,
            id: `draft-${Date.now()}-${Math.random()}`,
            referenceName: getImageReferenceName(filename),
            mediaType: mediaType
          }
        }
        const filename = img.filename || img.name || 'media'
        let mediaType = img.mediaType
        if (!mediaType) {
          // Detect from filename or URL
          if (filename.match(/\.pdf$/i) || img.url?.match(/\.pdf$/i)) {
            mediaType = 'pdf'
          } else if (img.url?.match(/\.(mp4|webm|mov)$/i) || filename.match(/\.(mp4|webm|mov)$/i)) {
            mediaType = 'video'
          } else {
            mediaType = 'image'
          }
        }
        return {
          url: img.url,
          preview: img.url,
          name: filename,
          filename: filename,
          size: img.size || 0,
          id: `draft-${Date.now()}-${Math.random()}`,
          uploadedAt: img.uploadedAt,
          storagePath: img.storagePath || null, // Preserve storagePath for cleanup!
          referenceName: img.referenceName || getImageReferenceName(filename),
          mediaType: mediaType
        }
      })

      // Lade den neuen Entwurf
      setFormData({
        deviceName: draft.deviceName || '',
        category: draft.category || 'troubleshooting',
        problemTitle: draft.problemTitle || '',
        errorCode: draft.errorCode || '',
        problemDescription: draft.problemDescription || '',
        solution: draft.solution || '',
        components: Array.isArray(draft.components)
          ? draft.components.join(', ')
          : draft.components || '',
        difficulty: draft.difficulty || 'medium',
        estimatedTime: draft.estimatedTime || '',
        toolsRequired: Array.isArray(draft.toolsRequired)
          ? draft.toolsRequired.join(', ')
          : draft.toolsRequired || '',
        images: draftImages
      })

      // Navigate to last saved step
      if (draft.lastSavedStep) {
        setCurrentStep(Math.min(draft.lastSavedStep, totalSteps))
      }

      setCurrentDraftId(draft.id)
      toast.success('Entwurf geladen')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Error loading draft:', error)
      toast.error('Fehler beim Laden des Entwurfs')
    } finally {
      setLoadingDraft(false)
    }
  }

  // Handle Quick-Add draft created
  const handleQuickAddDraftCreated = draftId => {
    // Refresh drafts list
    if (draftsListRefreshRef.current) {
      draftsListRefreshRef.current()
    }
    // Toast message is already shown in QuickAddModal, no need to show it again
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1ProblemDetails
            formData={formData}
            onChange={handleChange}
            disabled={submitting}
          />
        )
      case 2:
        return (
          <Step2Solution
            formData={formData}
            onChange={handleChange}
            disabled={submitting}
            companyId={user?.userData?.companyId}
          />
        )
      case 3:
        return (
          <Step3AdditionalInfo
            formData={formData}
            onChange={handleChange}
            disabled={submitting}
          />
        )
      case 4:
        return <Step4Review formData={formData} userEmail={user?.email} />
      default:
        return null
    }
  }

  return (
    <div className='contribute-page'>
      {features?.imageHeader && (
        <ImageHeader
          imageUrl={headerImageUrl}
          companyId={user?.userData?.companyId}
        />
      )}
      {(submitting || loadingDraft) && <LoadingSpinner loading={submitting || loadingDraft} />}

      <div className='contribute-header'>
        <div className='contribute-header-content'>
          <div>
            <h1>Wissen beitragen</h1>
            <p>Teilen Sie Ihre Erfahrung mit anderen Technikern</p>

          </div>
          <div className='contribute-header-actions'>
            {currentDraftId && (
              <button
                className='btn-new-draft'
                onClick={() => {
                  setFormData({
                    deviceName: '',
                    category: 'troubleshooting',
                    problemTitle: '',
                    errorCode: '',
                    problemDescription: '',
                    solution: '',
                    components: '',
                    difficulty: 'medium',
                    estimatedTime: '',
                    toolsRequired: '',
                    images: []
                  })
                  setCurrentStep(1)
                  setCurrentDraftId(null)
                  setError(null)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                title='Neuen Entwurf erstellen'
              >
                + Neuer Entwurf
              </button>
            )}
            <button
              className='btn-quick-add'
              onClick={() => setShowQuickAddModal(true)}
              title='Schnell hinzufügen'
            >
              + Schnell hinzufügen
            </button>
          </div>
        </div>
      </div>

      {/* Auto-Save Status */}
      {saveStatus !== 'idle' && (
        <div className={`auto-save-status auto-save-${saveStatus} show`}>
          {saveStatus === 'saving' && '💾 Wird gespeichert...'}
          {saveStatus === 'saved' && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircleIcon style={{ fontSize: '18px' }} />
              Gespeichert
            </span>
          )}
          {saveStatus === 'error' && '⚠ Fehler beim Speichern'}
        </div>
      )}

      {/* Drafts List */}
      <DraftsList onLoadDraft={handleLoadDraft} onRefresh={draftsListRefreshRef} currentDraftId={currentDraftId} />

      <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />

      {success && (
        <div className='success-banner'>
          Vielen Dank! Ihr Beitrag wird geprüft und dann veröffentlicht.
        </div>
      )}

      {error && <div className='error-banner'>{error}</div>}

      <div className='contribute-form'>
        {renderStep()}

        <div className='form-navigation'>
          {currentStep > 1 && (
            <button
              type='button'
              className='btn-secondary'
              onClick={handleBack}
              disabled={submitting}
            >
              Zurück
            </button>
          )}

          <div className='nav-spacer'></div>

          {currentStep < totalSteps ? (
            <button
              type='button'
              className='btn-primary'
              onClick={handleNext}
              disabled={!validateStep(currentStep)}
            >
              Weiter
            </button>
          ) : (
            <button
              type='button'
              className='btn-primary btn-submit'
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Wird eingereicht...' : 'Wissen einreichen'}
            </button>
          )}

        </div>
        <p className='contribute-header-hint'>
              Ihre Entwürfe werden automatisch gespeichert
            </p>
      </div>

      <div style={{ marginTop: '2rem' }}></div>
      <GamificationBadge userEmail={user?.email} onStatsLoad={setUserStats} />
      <Leaderboard currentUserEmail={user?.email} />

      {/* Quick-Add Modal */}
      <QuickAddModal
        isOpen={showQuickAddModal}
        onClose={() => setShowQuickAddModal(false)}
        onDraftCreated={handleQuickAddDraftCreated}
      />
    </div>
  )
}

export default ContributeKnowledgePage
